import React, { useState, useRef, useEffect } from 'react';
import Card from './Card';
import CloseIcon from '../icons/CloseIcon';

interface GachaScratchAnimationProps {
  isOpen: boolean;
  onComplete: (result: { itemId?: string; itemName: string; imageUrl?: string; isBlank?: boolean }) => void;
  onClose?: () => void; // 닫기 버튼용 (결과 처리 없이 닫기)
  result: { itemId?: string; itemName: string; imageUrl?: string; isBlank?: boolean };
  scratchImageUrl?: string; // 스크래치 레이어 이미지 URL (선택사항)
}

const GachaScratchAnimation: React.FC<GachaScratchAnimationProps> = ({
  isOpen,
  onComplete,
  onClose,
  result,
  scratchImageUrl
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isScratching, setIsScratching] = useState(false);
  const [scratchProgress, setScratchProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [scratchLayerDrawn, setScratchLayerDrawn] = useState(false);
  const resultImageRef = useRef<HTMLImageElement | null>(null);

  const drawScratchLayer = (): Promise<void> => {
    return new Promise((resolve) => {
      if (!canvasRef.current) {
        resolve();
        return;
      }
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve();
        return;
      }

      // canvas의 실제 표시 크기 가져오기 (뒤의 div와 동일한 크기)
      // canvas는 width: 100%, height: 100%로 설정되어 있으므로
      // getBoundingClientRect()로 실제 표시 크기를 가져옴
      const canvasRect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      // canvas의 실제 표시 크기 (뒤의 div와 동일해야 함)
      const displayWidth = Math.floor(canvasRect.width);
      const displayHeight = Math.floor(canvasRect.height);

      // canvas의 실제 크기 설정 (고해상도 지원)
      // CSS 크기는 이미 100%로 설정되어 있으므로 변경하지 않음
      canvas.width = displayWidth * dpr;
      canvas.height = displayHeight * dpr;

      // 컨텍스트 초기화 및 스케일 조정
      ctx.setTransform(1, 0, 0, 1, 0, 0); // 리셋
      ctx.scale(dpr, dpr);

      // 이미지가 제공된 경우 이미지 사용, 없으면 기본 그리기
      if (scratchImageUrl) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          if (!canvasRef.current) {
            resolve();
            return;
          }
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve();
            return;
          }

          const canvasRect = canvas.getBoundingClientRect();
          const dpr = window.devicePixelRatio || 1;
          const displayWidth = Math.floor(canvasRect.width);
          const displayHeight = Math.floor(canvasRect.height);

          canvas.width = displayWidth * dpr;
          canvas.height = displayHeight * dpr;
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.scale(dpr, dpr);

          // 이미지를 canvas 크기에 맞게 그리기
          ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
          resolve();
        };
        img.onerror = () => {
          // 이미지 로드 실패 시 기본 그리기로 폴백
          drawDefaultScratchLayer(ctx, displayWidth, displayHeight, resolve);
        };
        img.src = scratchImageUrl;
      } else {
        // 기본 그리기 (기존 코드)
        drawDefaultScratchLayer(ctx, displayWidth, displayHeight, resolve);
      }
    });
  };

  const drawDefaultScratchLayer = (ctx: CanvasRenderingContext2D, displayWidth: number, displayHeight: number, onComplete: () => void) => {
    // 배경 텍스처 이미지 로드
    const bgImg = new Image();
    bgImg.crossOrigin = 'anonymous';
    bgImg.onload = () => {
      // 배경 그리기
      ctx.drawImage(bgImg, 0, 0, displayWidth, displayHeight);

      // 로고 이미지 그리기
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.onload = () => {
        // 로고 비율 유지하면서 중앙에 배치
        const logoAspect = 85.01 / 20; // Logo.tsx의 기본 비율

        // 캔버스 크기의 60% 너비로 설정
        let drawWidth = displayWidth * 0.6;
        let drawHeight = drawWidth / logoAspect;

        // 텍스트 크기 및 간격
        const fontSize = Math.min(displayWidth * 0.05, 16);
        const textGap = 40;

        // 전체 높이 계산 (로고 + 간격 + 텍스트)
        const totalHeight = drawHeight + textGap + (fontSize * 1.2);

        // 그룹의 시작 Y 좌표 (전체를 수직 중앙 정렬)
        const startY = (displayHeight - totalHeight) / 2;

        // 로고 X 좌표 (수평 중앙)
        const x = (displayWidth - drawWidth) / 2;

        // 로고 효과 (음각 느낌을 위한 하이라이트)
        ctx.save();
        ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        ctx.globalAlpha = 0.9; // 약간 투명하게 하여 질감과 섞이게
        ctx.drawImage(logoImg, x, startY, drawWidth, drawHeight);
        ctx.restore();

        // "SCRATCH HERE" 텍스트 추가
        ctx.font = `700 ${fontSize}px Pretendard`; // 굵게
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        const textY = startY + drawHeight + textGap;
        const text = '긁어서 확인하세요';

        // 텍스트 외곽선 (가독성 확보)
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = 4;
        ctx.lineJoin = 'round';
        ctx.strokeText(text, displayWidth / 2, textY);

        // 텍스트 본문 (진한 회색)
        ctx.fillStyle = '#222222';
        ctx.fillText(text, displayWidth / 2, textY);
        
        // 완료 콜백 호출
        onComplete();
      };
      logoImg.onerror = () => {
        // 로고 로드 실패해도 완료 처리
        onComplete();
      };
      logoImg.src = `${process.env.PUBLIC_URL || ''}/images/icons/Logo.svg`;
    };
    // 텍스처 이미지 로드 실패 시 폴백 (기존 그라데이션)
    bgImg.onerror = () => {
      const baseGradient = ctx.createLinearGradient(0, 0, displayWidth, displayHeight);
      baseGradient.addColorStop(0, '#E0E0E0');
      baseGradient.addColorStop(0.2, '#F5F5F5');
      baseGradient.addColorStop(0.4, '#C0C0C0');
      baseGradient.addColorStop(0.6, '#E0E0E0');
      baseGradient.addColorStop(0.8, '#D3D3D3');
      baseGradient.addColorStop(1, '#A9A9A9');
      ctx.fillStyle = baseGradient;
      ctx.fillRect(0, 0, displayWidth, displayHeight);

      // 로고 로드 (중복 코드지만 에러 처리를 위해)
      const logoImg = new Image();
      logoImg.src = `${process.env.PUBLIC_URL || ''}/images/icons/Logo.svg`;
      logoImg.onload = () => {
        const logoAspect = 85.01 / 20;
        let drawWidth = displayWidth * 0.6;
        let drawHeight = drawWidth / logoAspect;
        const fontSize = Math.min(displayWidth * 0.05, 16);
        const textGap = 40;
        const totalHeight = drawHeight + textGap + (fontSize * 1.2);
        const startY = (displayHeight - totalHeight) / 2;
        const x = (displayWidth - drawWidth) / 2;

        ctx.drawImage(logoImg, x, startY, drawWidth, drawHeight);

        ctx.font = `700 ${fontSize}px Pretendard`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        const textY = startY + drawHeight + textGap;
        const text = '긁어서 확인하세요';

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = 4;
        ctx.strokeText(text, displayWidth / 2, textY);

        ctx.fillStyle = '#222222';
        ctx.fillText(text, displayWidth / 2, textY);
        
        // 완료 콜백 호출
        onComplete();
      };
      logoImg.onerror = () => {
        // 로고 로드 실패해도 완료 처리
        onComplete();
      };
    };
    bgImg.src = `${process.env.PUBLIC_URL || ''}/images/scratch_texture.png`;
  };

  // 초기화 및 은박 레이어 그리기
  useEffect(() => {
    if (!isOpen) {
      setScratchLayerDrawn(false);
      setIsComplete(false);
      setScratchProgress(0);
      return;
    }

    // 은박 레이어를 먼저 그리고, 완전히 그려진 후에 결과 이미지 표시
    setScratchLayerDrawn(false);
    const timer = setTimeout(async () => {
      if (canvasRef.current) {
        // 은박 레이어 그리기 완료 대기
        await drawScratchLayer();
        // 은박 레이어가 완전히 그려진 후에 결과 이미지 표시
        setScratchLayerDrawn(true);
      }
    }, 50);

    // 리사이즈 이벤트 리스너 추가
    const handleResize = () => {
      if (canvasRef.current) {
        drawScratchLayer().catch(() => {
          // 에러 무시 (리사이즈 중 에러는 치명적이지 않음)
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, scratchImageUrl]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsScratching(true);
    scratch(e);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // 마우스를 누르고 있거나, 그냥 움직이기만 해도 긁히도록
    if (isScratching || e.buttons === 1) {
      scratch(e);
    }
  };

  const handleMouseUp = () => {
    setIsScratching(false);
  };

  const handleMouseLeave = () => {
    setIsScratching(false);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    setIsScratching(true);
    scratchTouch(e);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (isScratching) {
      scratchTouch(e);
    }
  };

  const handleTouchEnd = () => {
    setIsScratching(false);
  };

  const scratch = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || isComplete) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    // 정확한 좌표 계산 (스케일이 이미 적용되어 있으므로 그대로 사용)
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // 경계 체크
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;

    // 복권 긁기 효과 (더 부드러운 원형 브러시)
    ctx.globalCompositeOperation = 'destination-out';

    // 부드러운 그라데이션 브러시 효과
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, 40);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
    gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.8)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, 40, 0, Math.PI * 2);
    ctx.fill();

    // 추가로 작은 원으로 더 선명하게
    ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    ctx.beginPath();
    ctx.arc(x, y, 25, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalCompositeOperation = 'source-over';

    // 스크래치 진행도 계산
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    let transparentPixels = 0;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] < 10) { // 거의 투명한 픽셀도 카운트
        transparentPixels++;
      }
    }
    const progress = (transparentPixels / (pixels.length / 4)) * 100;
    setScratchProgress(progress);

    // 80% 이상 긁으면 완료 상태로 변경 (확인 버튼 표시)
    if (progress > 80 && !isComplete) {
      setIsComplete(true);
    }
  };

  const scratchTouch = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current || isComplete) return;
    e.preventDefault(); // 기본 스크롤 방지
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    if (!touch) return;

    // 정확한 좌표 계산 (스케일이 이미 적용되어 있으므로 그대로 사용)
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    // 경계 체크
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;

    // 복권 긁기 효과 (더 부드러운 원형 브러시)
    ctx.globalCompositeOperation = 'destination-out';

    // 부드러운 그라데이션 브러시 효과
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, 40);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
    gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.8)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, 40, 0, Math.PI * 2);
    ctx.fill();

    // 추가로 작은 원으로 더 선명하게
    ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    ctx.beginPath();
    ctx.arc(x, y, 25, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalCompositeOperation = 'source-over';

    // 스크래치 진행도 계산
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    let transparentPixels = 0;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] < 10) { // 거의 투명한 픽셀도 카운트
        transparentPixels++;
      }
    }
    const progress = (transparentPixels / (pixels.length / 4)) * 100;
    setScratchProgress(progress);

    // 80% 이상 긁으면 완료 상태로 변경 (확인 버튼 표시)
    if (progress > 80 && !isComplete) {
      setIsComplete(true);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      fontFamily: 'Pretendard'
    }}>
      <Card
        width="450px"
        padding="0"
        borderRadius="16px"
        background="var(--Color-background-normal, white)"
        boxShadow="0px 8px 16px rgba(0, 0, 0, 0.12)"
        style={{
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{
          width: '100%',
          padding: '28px',
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          alignItems: 'center'
        }}>
          {/* 헤더 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '8px',
            width: '100%'
          }}>
            <h2 style={{
              margin: 0,
              color: 'var(--Color-text-normal, #161616)',
              fontSize: '20px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              lineHeight: '30px'
            }}>
              스크래치 뽑기
            </h2>

            {/* 닫기 버튼 */}
            <div
              style={{
                padding: '4px',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => {
                // 닫기 버튼을 누르면 결과 처리 없이 다이얼로그만 닫기
                if (onClose) {
                  onClose();
                } else {
                  // onClose가 없으면 완료된 경우에만 onComplete 호출
                  if (isComplete) {
                    onComplete(result);
                  }
                }
              }}
            >
              <CloseIcon width={13.20} height={13.20} color="#0D0D0D" />
            </div>
          </div>

          <div style={{
            position: 'relative',
            width: '100%',
            height: '296px',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '2px solid var(--Color-line-normal, #F0F0F0)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* 결과 아이템 (스크래치 아래) - 은박 레이어가 그려진 후에만 표시 */}
            <div style={{
              display: scratchLayerDrawn ? 'flex' : 'none',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              width: '100%',
              height: '100%',
              background: result.isBlank ? '#FFEBEE' : '#E8F5E9',
              pointerEvents: 'none',
              zIndex: 1,
              position: 'absolute',
              top: 0,
              left: 0
            }}>
              {result.isBlank ? (
                <>
                  <span style={{ fontSize: '64px' }}>❌</span>
                  <div style={{
                    color: '#C62828',
                    fontSize: '24px',
                    fontFamily: 'pretendard',
                    fontWeight: '600'
                  }}>
                    꽝
                  </div>
                </>
              ) : (
                <>
                  {result.imageUrl ? (
                    <div style={{
                      position: 'relative',
                      width: '120px',
                      height: '120px'
                    }}>
                      <img
                        ref={resultImageRef}
                        src={result.imageUrl}
                        alt={result.itemName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  ) : (
                    <span style={{ fontSize: '64px' }}>📦</span>
                  )}
                  <div style={{
                    color: '#2E7D32',
                    fontSize: '24px',
                    fontFamily: 'pretendard',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    {result.itemName}
                  </div>
                </>
              )}
            </div>

            {/* Canvas는 absolute로 위에 덮기 - 뒤의 div와 정확히 같은 크기 */}
            {/* 은박 레이어가 그려지기 전에는 배경색으로 완전히 가리기 */}
            {!scratchLayerDrawn && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: '#E0E0E0',
                zIndex: 3,
                pointerEvents: 'none'
              }} />
            )}
            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                touchAction: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                zIndex: 2,
                backgroundColor: scratchLayerDrawn ? 'transparent' : '#E0E0E0'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
          </div>

          {/* 안내 텍스트 */}
          <div style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: '14px',
            fontFamily: 'Pretendard',
            fontWeight: '400',
            textAlign: 'center',
            width: '100%'
          }}>
            {isComplete ? '확인 버튼을 눌러주세요' : '스크래치를 긁어서 결과를 확인하세요'}
          </div>


          {/* 확인 버튼 (항상 표시, 80% 이상 긁으면 활성화) */}
          <button
            onClick={() => {
              if (isComplete) {
                onComplete(result);
              }
            }}
            disabled={!isComplete}
            style={{
              padding: '12px 32px',
              borderRadius: '8px',
              background: isComplete ? 'var(--Color-primary-normal, #4F46E5)' : 'var(--Color-line-normal, #F0F0F0)',
              color: isComplete ? 'white' : 'var(--Color-text-assistive, #989898)',
              border: 'none',
              cursor: isComplete ? 'pointer' : 'not-allowed',
              fontSize: '16px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              transition: 'background 0.2s, color 0.2s',
              minWidth: '120px',
              width: '100%',
              opacity: isComplete ? 1 : 0.6
            }}
            onMouseOver={(e) => {
              if (isComplete) {
                e.currentTarget.style.background = 'var(--Color-primary-hover, #4338CA)';
              }
            }}
            onMouseOut={(e) => {
              if (isComplete) {
                e.currentTarget.style.background = 'var(--Color-primary-normal, #4F46E5)';
              }
            }}
          >
            {isComplete ? '확인' : '스크래치를 긁어주세요'}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default GachaScratchAnimation;


